import {onLocationChanged as onLocationChangedAction} from 'actions/router-actions';
import isEqualWith from 'lodash/isEqualWith';
import React, {PureComponent} from 'react';
import {ReactReduxContext} from 'react-redux';
import {Router} from 'react-router';
import {connect} from 'redux-store';
import {createSelectors} from './selectors';

import type {Store, Unsubscribe} from '@reduxjs/toolkit';
import type {History, UnregisterCallback} from 'history';
import type {Context} from 'react';
import type {MapDispatchToPropsParam, ReactReduxContextValue} from 'react-redux';

type DispatchProps = {
    onLocationChanged: unknown;
};

type ConnectedRouterProps<LocationState = unknown> = {
    store: Store<unknown>;
    history: History<LocationState>;
    basename?: string | undefined;
    children?: React.ReactNode | undefined;
    noInitialPop?: boolean | undefined;
    noTimeTravelDebugging?: boolean | undefined;
    stateCompareFunction?: () => void | undefined;
    omitRouter?: boolean | undefined;
};

type ConnectedRouterWithContextProps = Omit<ConnectedRouterProps, 'store'> & {
    context?: Context<ReactReduxContextValue> | undefined;
};

export const createConnectedRouter = () => {
    const {getLocation} = createSelectors();

    /*
     * ConnectedRouter listens to a history object passed from props.
     * When history is changed, it dispatches action to redux store.
     * Then, store will pass props to component to render.
     * This creates uni-directional flow from history->store->router->components.
     */

    class ConnectedRouter extends PureComponent<ConnectedRouterProps> {
        private inTimeTravelling: boolean;
        private readonly unsubscribe: Unsubscribe;
        private readonly unlisten: UnregisterCallback;

        constructor(props) {
            super(props);

            const {store, history, onLocationChanged, stateCompareFunction} = props;

            this.inTimeTravelling = false;

            // Subscribe to store changes to check if we are in time travelling
            this.unsubscribe = store.subscribe(() => {
                // Allow time travel debugging compatibility to be turned off
                // as the detection for this (below) is error prone in apps where the
                // store may be unmounted, a navigation occurs, and then the store is re-mounted
                // during the app's lifetime. Detection could be much improved if Redux DevTools
                // simply set a global variable like `REDUX_DEVTOOLS_IS_TIME_TRAVELLING=true`.
                const isTimeTravelDebuggingAllowed = !props.noTimeTravelDebugging;

                // Extract store's location
                const {
                    pathname: pathnameInStore,
                    search: searchInStore,
                    hash: hashInStore,
                    state: stateInStore,
                } = getLocation(store.getState());
                // Extract history's location
                const {
                    pathname: pathnameInHistory,
                    search: searchInHistory,
                    hash: hashInHistory,
                    state: stateInHistory,
                } = history.location;

                // If we do time travelling, the location in store is changed but location in history is not changed
                if (
                    isTimeTravelDebuggingAllowed &&
                    props.history.action === 'PUSH' &&
                    (pathnameInHistory !== pathnameInStore ||
                        searchInHistory !== searchInStore ||
                        hashInHistory !== hashInStore ||
                        !isEqualWith(stateInStore, stateInHistory, stateCompareFunction))
                ) {
                    this.inTimeTravelling = true;
                    // Update history's location to match store's location
                    history.push({
                        pathname: pathnameInStore,
                        search: searchInStore,
                        hash: hashInStore,
                        state: stateInStore,
                    });
                }
            });

            const handleLocationChange = (location, action, isFirstRendering = false) => {
                // Dispatch onLocationChanged except when we're in time travelling
                if (!this.inTimeTravelling) {
                    onLocationChanged(location, action, isFirstRendering);
                } else {
                    this.inTimeTravelling = false;
                }
            };

            // Listen to history changes
            this.unlisten = history.listen(handleLocationChange);

            if (!props.noInitialPop) {
                // Dispatch a location change action for the initial location.
                // This makes it backward-compatible with react-router-redux.
                // But, we add `isFirstRendering` to `true` to prevent double-rendering.
                handleLocationChange(history.location, history.action, true);
            }
        }

        componentWillUnmount() {
            this.unlisten();
            this.unsubscribe();
        }

        render() {
            const {omitRouter, history, children} = this.props;

            // The `omitRouter` option is available for applications that must
            // have a Router instance higher in the component tree but still desire
            // to use connected-react-router for its Redux integration.
            if (omitRouter) {
                return <React.Fragment>{children}</React.Fragment>;
            }

            return <Router history={history}>{children}</Router>;
        }
    }

    const mapDispatchToProps: MapDispatchToPropsParam<DispatchProps, ConnectedRouterWithContextProps> = (dispatch) => ({
        onLocationChanged: (location, action, isFirstRendering) =>
            dispatch(onLocationChangedAction(location, action, isFirstRendering)),
    });

    const ConnectedRouterWithContext = (props: ConnectedRouterWithContextProps) => {
        const Context = props.context || ReactReduxContext;

        if (Context === null) {
            throw new Error('Please upgrade to react-redux v6');
        }

        return <Context.Consumer>{({store}) => <ConnectedRouter store={store} {...props} />}</Context.Consumer>;
    };

    return connect<never, DispatchProps, ConnectedRouterWithContextProps>(
        null,
        mapDispatchToProps,
    )(ConnectedRouterWithContext);
};
